import "@/App.css";
import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FormContextProvider } from "@/components/context/useFormContext";
import {
	ALL_LOAN_TYPES,
	FIX_MY_CREDIT_NAME,
	LOAN_TYPE_BIKE,
	LOAN_TYPE_BOAT,
	LOAN_TYPE_BUSINESS,
	LOAN_TYPE_CAR,
	LOAN_TYPE_CARAVAN,
	LOAN_TYPE_EQUIPMENT,
	LOAN_TYPE_MORTGAGE,
	LOAN_TYPE_MOTORCYCLE,
	LOAN_TYPE_PERSONAL,
	LOAN_TYPE_TRUCK,
	WEDO_INV_FIN_NAME,
} from "@/lib/constants";
import { QuickFormMain } from "@/components/forms/quickForm/quickFormMain";
import { FixMyCredit } from "@/components/forms/fmc/fixMyCreditForm";
import FormSkeleton from "@/components/ui/formSkeleton";
import usePageLoading from "@/lib/usePageLoading";
import { WeDoInvFin } from "@/components/forms/wif/weDoInvoiceFinance";

const queryClient = new QueryClient();

type AppProps = {
	rootElement: HTMLElement;
};

export default function App({ rootElement }: AppProps) {
	const pageLoading = usePageLoading();
	const [component, setComponent] = useState<string | undefined>(undefined);
	const [siteTitle, setSiteTitle] = useState<string | undefined>(undefined);
	const [loan, setLoan] = useState<string | undefined>(undefined);

	useEffect(() => {
		const container = rootElement?.dataset.component;
		const siteTitle = rootElement?.dataset.title;

		switch (container) {
			case "main":
				setLoan(ALL_LOAN_TYPES);
				break;
			case "car":
				setLoan(LOAN_TYPE_CAR);
				break;
			case "boat":
				setLoan(LOAN_TYPE_BOAT);
				break;
			case "caravan":
				setLoan(LOAN_TYPE_CARAVAN);
				break;
			case "truck":
				setLoan(LOAN_TYPE_TRUCK);
				break;
			case "equipment":
				setLoan(LOAN_TYPE_EQUIPMENT);
				break;
			case "personal":
				setLoan(LOAN_TYPE_PERSONAL);
				break;
			case "bike":
				setLoan(LOAN_TYPE_BIKE);
				break;
			case "motorcycle":
				setLoan(LOAN_TYPE_MOTORCYCLE);
				break;
			case "business":
				setLoan(LOAN_TYPE_BUSINESS);
				break;
			case "credit":
				setLoan(FIX_MY_CREDIT_NAME);
				break;
			case "invoice":
				setLoan(WEDO_INV_FIN_NAME);
				break;
			case "mortgage":
				setLoan(LOAN_TYPE_MORTGAGE);
				break;
			default:
				setLoan(ALL_LOAN_TYPES);
				break;
		}

		setSiteTitle(siteTitle);
		setComponent(container);
	}, [rootElement, siteTitle, loan]);

	const renderComponent = () => {
		if (siteTitle?.includes("Contact") && loan === FIX_MY_CREDIT_NAME) {
			return <FixMyCredit rootId={rootElement} loanTypes={loan} siteTitle={siteTitle} />;
		} else if (siteTitle?.includes("Contact") && loan === WEDO_INV_FIN_NAME) {
			return <WeDoInvFin rootId={rootElement} loanTypes={loan} siteTitle={siteTitle} />;
		} else {
			return pageLoading && component === "main" ? (
				<FormSkeleton />
			) : (
				<QuickFormMain rootId={rootElement} loanTypes={loan} siteTitle={siteTitle} />
			);
		}
	};

	return (
		<FormContextProvider id={rootElement}>
			<QueryClientProvider client={queryClient}>
				{siteTitle ? renderComponent() : <div>Loading...</div>}
			</QueryClientProvider>
		</FormContextProvider>
	);
}
